import { memo, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';

import { CategoryBadge } from '@components/CategoryBadge';
import { HaloLink } from '@components/HaloLink';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import LazyVideo from '@components/LazyVideo';
import { useLowDataMode, useStationMode } from '@hooks/station';
import { extraQueryParams } from '@utils/constants.ts';
import { sendSelectCompanionMessage } from '@utils/station';
import { Companion } from '../../types/index.ts';

interface CompanionCardProps {
  companion: Companion;
  className?: string;
  compactSize?: boolean;
  showFavoriteButton?: boolean;
  isFavorited?: boolean;
  isLoading?: boolean;
  onFavoriteClick?: (e: React.MouseEvent) => void;
}

export const CompanionCard: React.FC<CompanionCardProps> = memo(
  ({
    companion,
    className,
    compactSize = false,
    showFavoriteButton = false,
    isFavorited = false,
    isLoading = false,
    onFavoriteClick,
  }: CompanionCardProps) => {
    const { isAuthenticated, signinRedirect } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isStationMode = useStationMode();
    const [isLowDataMode] = useLowDataMode();

    const handleCardClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (!isAuthenticated) {
          const companionVideoUrl =
            companion.videoLoopUrl || companion.videoIdleLoopUrl;
          const companionImageUrl =
            companion.previewUrl || companion.previewImageUrl;

          signinRedirect({
            extraQueryParams: {
              ...extraQueryParams,
              ...(companionImageUrl && {
                backgroundImageUrl: companionImageUrl,
              }),
              ...(companionVideoUrl && { backgroundUrl: companionVideoUrl }),
            },
            state: { returnUrl: `/${companion.slug}?start=true` },
          }).catch((error) => {
            console.error('Error during sign-in redirect:', error);
          });
          return;
        }
        if (isStationMode) {
          sendSelectCompanionMessage(companion.mentorId);
          return;
        }
        navigate(`/${companion.slug}?start=true`, {
          state: {
            prevLocation: `${location.pathname}${location.search}${location.hash}`,
          },
        });
      },
      [
        isAuthenticated,
        companion.slug,
        companion.mentorId,
        companion.videoLoopUrl,
        companion.videoIdleLoopUrl,
        companion.previewImageUrl,
        companion.previewUrl,
        navigate,
        isStationMode,
        signinRedirect,
        location.pathname,
        location.search,
        location.hash,
      ],
    );

    return (
      <div className={`flex snap-start relative group  ${className}`}>
        <div
          onClick={handleCardClick}
          className="absolute  inset-0  z-[100] cursor-pointer"
        />
        {/* Top right - HaloLink only */}
        <div className="absolute top-5 right-5 z-[101] flex items-center gap-2">
          <HaloLink mentorId={companion.mentorId} showLink />
        </div>
        <CategoryBadge category="companion" />
        <div
          className={`flex flex-col justify-between w-full p-5 rounded-[16px] shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] relative before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,1)_100%)] before:h-full before:w-full before:z-[99] before:rounded-[16px] cursor-pointer hover:shadow-[0_4px_20px_0_rgba(0,0,0,0.50)] transition-all duration-300 opacity-100 ${
            compactSize ? 'h-[395px]' : 'h-[520px]'
          }`}>
          {/* Background Video */}
          <div className="flex absolute top-0 left-0 w-full h-full rounded-[16px] overflow-hidden">
            {isLowDataMode ? (
              <img
                className="h-full w-full object-cover"
                src={companion.previewImageUrl || companion.previewUrl || ''}
                alt=""
              />
            ) : (
              <LazyVideo
                className="h-full w-full object-cover"
                src={companion.videoLoopUrl || companion.videoIdleLoopUrl || ''}
                poster={
                  companion.previewImageUrl || companion.previewUrl || undefined
                }
                muted
                autoPlay={true}
                loop
                playsInline
                webkit-playsinline="true"
                preload="metadata"
                aria-label={`${companion.firstName} ${companion.lastName}`}
                role="img"
                aria-hidden="true"
                enabled={true}
                rootMargin="300px"
                threshold={0.1}
              />
            )}
          </div>

          {/* Companion Info */}
          <div className="absolute gap-[10px] z-[100] bottom-0 left-0 right-0 mx-auto my-0 h-fit flex flex-col justify-center items-start rounded-[16px] px-5 pb-5">
            {/* Call to Action */}
            <div className="text-white text-[20px] md:text-[24px] text-left font-normal leading-[1.2] w-full font-[DM_Serif_Display] tracking-[0%] line-clamp-3 break-words [overflow-wrap:anywhere] pb-[2px]">
              {companion.call_to_action
                ? companion.call_to_action
                : companion.headline}
            </div>

            {/* Name, Job Title, and Favorite Button */}
            <div className="flex items-start justify-between w-full gap-2">
              <div className="flex items-start gap-[6px] flex-1 min-w-0">
                <span className="text-white font-[DM_Sans] font-[500] font-medium text-[14px] md:text-[16px] leading-[1.25] tracking-[-1%] text-left whitespace-normal break-words [overflow-wrap:anywhere]">
                  {companion.firstName} {companion.lastName}
                </span>
                {companion.jobTitle && (
                  <>
                    <span className="text-[15px] text-[#4A4A4A] w-[4px] h-[4px] rounded-full bg-[#4A4A4A]"></span>
                    <span className="text-[#7D7D7D] font-[DM_Sans] font-[400] text-[14px] md:text-[16px] leading-[1.25] tracking-[0%] text-left whitespace-normal break-words [overflow-wrap:anywhere]">
                      {companion.jobTitle}
                    </span>
                  </>
                )}
              </div>

              {/* Favorite Button - Always visible like MusicCard */}
              {showFavoriteButton && onFavoriteClick && (
                <div
                  className="flex items-center justify-center w-5 h-5 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFavoriteClick(e);
                  }}>
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : isFavorited ? (
                    <Likedicon width={20} height={20} />
                  ) : (
                    <FavoriteIcon width={20} height={20} fill="white" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CompanionCard.displayName = 'CompanionCard';
